/**
 * 에디터 본문의 인라인(base64) 이미지 처리. 고도몰 이관 상세에 base64 이미지가 박혀 상세 HTML 이 최대 14MB 까지
 * 커졌다(쇼핑몰 상세 지연·상품 엑셀 메모리 부족). 이관팀이 S3 파일로 바꿨고, 에디터는 다시 들어오지 않게 막는다.
 * 원칙: base64 는 저장으로 내보내지 않는다 — 올릴 수 있으면 S3 로 올려 URL 로 바꾸고, 못 올리면 빼고 알린다.
 */

/** <img ... src="data:image/...;base64,..."> — 따옴표 있음/없음 모두. 1번 그룹 = data URL. */
const INLINE_IMG_RE = /<img\b[^>]*?\bsrc\s*=\s*(?:"(data:image\/[^"]+)"|'(data:image\/[^']+)'|(data:image\/[^\s>]+))[^>]*>/gi;

const dataUrlsOf = (html: string): string[] => {
  const out: string[] = [];
  for (const m of html.matchAll(INLINE_IMG_RE)) {
    const url = m[1] ?? m[2] ?? m[3];
    if (url && !out.includes(url)) out.push(url);
  }
  return out;
};

export const hasInlineImage = (html: string | null | undefined): boolean =>
  Boolean(html) && /\bsrc\s*=\s*["']?data:image\//i.test(html as string);

/** base64 이미지 태그를 통째로 뺀다. */
export const stripInlineImages = (html: string): string => html.replace(INLINE_IMG_RE, '');

const EXT: Record<string, string> = { 'image/png': 'png', 'image/jpeg': 'jpg', 'image/jpg': 'jpg', 'image/gif': 'gif', 'image/webp': 'webp', 'image/svg+xml': 'svg' };

/** data URL → File(업로드용). base64 가 아니거나 깨졌으면 null. */
export const dataUrlToFile = (dataUrl: string, index: number): File | null => {
  const m = /^data:(image\/[a-z0-9.+-]+);base64,(.+)$/i.exec(dataUrl.trim());
  if (!m) return null;
  const mime = (m[1] ?? 'image/png').toLowerCase();
  try {
    const bin = atob((m[2] ?? '').replace(/\s/g, ''));
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new File([bytes], `pasted-${Date.now()}-${index + 1}.${EXT[mime] ?? 'png'}`, { type: mime });
  } catch {
    return null;
  }
};

export interface InlineImageResult {
  html: string;
  /** S3 로 올려 URL 로 바꾼 개수. */
  uploaded: number;
  /** 올리지 못해(업로더 없음·실패·깨진 데이터) 뺀 개수. */
  dropped: number;
}

/**
 * base64 이미지를 S3 URL 로 바꾼다. upload 가 없거나 실패한 이미지는 태그째 뺀다(저장으로 내보내지 않음).
 * 같은 이미지가 여러 번 나오면 한 번만 올린다.
 */
export const replaceInlineImages = async (
  html: string,
  upload: ((file: File) => Promise<string>) | undefined,
): Promise<InlineImageResult> => {
  const urls = dataUrlsOf(html);
  if (urls.length === 0) return { html, uploaded: 0, dropped: 0 };
  const mapped = new Map<string, string>();
  let uploaded = 0;
  let dropped = 0;
  for (const [i, dataUrl] of urls.entries()) {
    const file = upload ? dataUrlToFile(dataUrl, i) : null;
    if (!file || !upload) {
      dropped += 1;
      continue;
    }
    try {
      mapped.set(dataUrl, await upload(file));
      uploaded += 1;
    } catch {
      dropped += 1;
    }
  }
  const out = html.replace(INLINE_IMG_RE, (tag, a?: string, b?: string, c?: string) => {
    const dataUrl = a ?? b ?? c ?? '';
    const url = mapped.get(dataUrl);
    return url ? tag.replace(dataUrl, url) : '';
  });
  return { html: out, uploaded, dropped };
};

/** 클립보드·끌어다 놓기의 이미지 파일만. */
export const imageFilesOf = (list: FileList | null | undefined): File[] =>
  Array.from(list ?? []).filter((f) => f.type.startsWith('image/'));
